// Notion API client setup
// This will be used to sync data with Notion databases

import type { Comment, CommentsResponse } from './types'

export interface NotionTask {
  id: string
  title: string
  status: "Backlog" | "Not Started" | "In Progress" | "Done" | "Quality Check"
  due: Date | null
  contributors: string[]
  description: string
}

export interface NotionTimesheet {
  id: string
  taskId: string
  userId: string
  status: "Draft" | "Submitted"
  startTime: Date
  endTime: Date | null
  manualMinutes: number
  isSubmitted: boolean
}

// Notion API functions (to be implemented with actual Notion API)
export async function fetchUserTasks(_userId: string): Promise<NotionTask[]> {
  // TODO: Implement actual Notion API call
  // For now, return dummy data
  return [
    {
      id: "task-1",
      title: "Design Homepage Mockups",
      status: "In Progress",
      due: new Date("2025-10-25"),
      contributors: ["user-1"],
      description:
        "Create high-fidelity mockups for the new homepage design including hero section, features, and testimonials.",
    },
    {
      id: "task-2",
      title: "Implement Authentication System",
      status: "Not Started",
      due: new Date("2025-10-28"),
      contributors: ["user-1"],
      description:
        "Set up user authentication with email/password and OAuth providers. Include password reset functionality.",
    },
    {
      id: "task-3",
      title: "Database Schema Design",
      status: "Done",
      due: new Date("2025-10-20"),
      contributors: ["user-1"],
      description:
        "Design and document the complete database schema for the application including all tables and relationships.",
    },
    {
      id: "task-4",
      title: "API Documentation",
      status: "Quality Check",
      due: new Date("2025-10-24"),
      contributors: ["user-1"],
      description: "Complete API documentation for all endpoints with examples and error codes.",
    },
    {
      id: "task-5",
      title: "User Dashboard UI",
      status: "In Progress",
      due: new Date("2025-10-26"),
      contributors: ["user-1"],
      description: "Build the main user dashboard with widgets for analytics, recent activity, and quick actions.",
    },
    {
      id: "task-6",
      title: "Payment Integration",
      status: "Not Started",
      due: new Date("2025-11-01"),
      contributors: ["user-1"],
      description: "Integrate Stripe payment processing for subscriptions and one-time purchases.",
    },
    {
      id: "task-7",
      title: "Email Templates",
      status: "Done",
      due: new Date("2025-10-18"),
      contributors: ["user-1"],
      description: "Design and implement responsive email templates for notifications and marketing.",
    },
    {
      id: "task-8",
      title: "Mobile Responsive Design",
      status: "In Progress",
      due: new Date("2025-10-27"),
      contributors: ["user-1"],
      description: "Ensure all pages are fully responsive and optimized for mobile devices.",
    },
    {
      id: "task-9",
      title: "Search Functionality",
      status: "Quality Check",
      due: new Date("2025-10-23"),
      contributors: ["user-1"],
      description: "Implement full-text search with filters and sorting options.",
    },
    {
      id: "task-10",
      title: "User Profile Settings",
      status: "Done",
      due: new Date("2025-10-19"),
      contributors: ["user-1"],
      description: "Create user profile page with editable settings and preferences.",
    },
    {
      id: "task-11",
      title: "Notification System",
      status: "In Progress",
      due: new Date("2025-10-29"),
      contributors: ["user-1"],
      description: "Build real-time notification system with email and in-app notifications.",
    },
    {
      id: "task-12",
      title: "Admin Panel",
      status: "Not Started",
      due: new Date("2025-11-05"),
      contributors: ["user-1"],
      description: "Create admin panel for user management, analytics, and system configuration.",
    },
    {
      id: "task-13",
      title: "Performance Optimization",
      status: "Quality Check",
      due: new Date("2025-10-25"),
      contributors: ["user-1"],
      description: "Optimize application performance including lazy loading, caching, and code splitting.",
    },
    {
      id: "task-14",
      title: "Security Audit",
      status: "Not Started",
      due: new Date("2025-11-10"),
      contributors: ["user-1"],
      description: "Conduct comprehensive security audit and implement recommended fixes.",
    },
    {
      id: "task-15",
      title: "Data Export Feature",
      status: "Done",
      due: new Date("2025-10-21"),
      contributors: ["user-1"],
      description: "Allow users to export their data in CSV and JSON formats.",
    },
    {
      id: "task-16",
      title: "Analytics Dashboard",
      status: "In Progress",
      due: new Date("2025-10-30"),
      contributors: ["user-1"],
      description: "Build comprehensive analytics dashboard with charts and insights.",
    },
    {
      id: "task-17",
      title: "API Rate Limiting",
      status: "Done",
      due: new Date("2025-10-17"),
      contributors: ["user-1"],
      description: "Implement rate limiting for API endpoints to prevent abuse.",
    },
    {
      id: "task-18",
      title: "Onboarding Flow",
      status: "Quality Check",
      due: new Date("2025-10-26"),
      contributors: ["user-1"],
      description: "Create interactive onboarding flow for new users with tutorials.",
    },
    {
      id: "task-19",
      title: "File Upload System",
      status: "In Progress",
      due: new Date("2025-11-02"),
      contributors: ["user-1"],
      description: "Implement secure file upload with validation and storage management.",
    },
    {
      id: "task-20",
      title: "Internationalization",
      status: "Not Started",
      due: new Date("2025-11-15"),
      contributors: ["user-1"],
      description: "Add multi-language support with translation management system.",
    },
    {
      id: "task-21",
      title: "Testing Suite",
      status: "In Progress",
      due: new Date("2025-10-28"),
      contributors: ["user-1"],
      description: "Write comprehensive unit and integration tests for all features.",
    },
    {
      id: "task-22",
      title: "CI/CD Pipeline",
      status: "Done",
      due: new Date("2025-10-16"),
      contributors: ["user-1"],
      description: "Set up automated CI/CD pipeline with testing and deployment.",
    },
  ]
}

export async function fetchUserTimesheets(_userId: string): Promise<NotionTimesheet[]> {
  // TODO: Implement actual Notion API call
  // For now, return dummy data
  const entries: NotionTimesheet[] = []
  const taskIds = Array.from({ length: 22 }, (_, i) => `task-${i + 1}`)

  // Generate 50+ entries across different dates and tasks
  for (let i = 0; i < 55; i++) {
    const dayOffset = Math.floor(i / 3)
    const date = new Date("2025-10-22")
    date.setDate(date.getDate() - dayOffset)

    const startHour = 9 + (i % 8)
    const duration = 1 + Math.floor(Math.random() * 3)

    const startTime = new Date(date)
    startTime.setHours(startHour, 0, 0, 0)

    const endTime = new Date(startTime)
    endTime.setHours(startHour + duration, 0, 0, 0)

    const taskId = taskIds[i % taskIds.length]
    const isOld = dayOffset > 5

    entries.push({
      id: `timesheet-${i + 1}`,
      taskId,
      userId: "user-1",
      status: isOld ? "Submitted" : i % 4 === 0 ? "Draft" : "Submitted",
      startTime,
      endTime,
      manualMinutes: i % 5 === 0 ? 15 : 0,
      isSubmitted: isOld ? true : i % 4 !== 0,
    })
  }

  return entries
}

// --------------------
// Client-side helpers to fetch via WordPress -> n8n webhook proxy
// --------------------
export interface NotionSyncResponse {
  requestId?: string
  status?: 'ok' | 'error'
  generatedAt?: string
  meta?: Record<string, any>
  data?: {
    tasks?: NotionTask[]
    timesheets?: NotionTimesheet[]
  }
}

export async function fetchNotionDataFromWP(opts?: { since?: string; until?: string; force?: boolean }): Promise<NotionSyncResponse> {
  const isDevMode = (window as any).WP_COCKPIT?.devMode || false
  
  // Create cache key based on parameters
  const cacheKey = `notion_data_${opts?.since || 'all'}_${opts?.until || 'all'}`
  
  // Check in-memory cache first if not forcing refresh
  if (!opts?.force) {
    const cached = notionDataCache.get(cacheKey)
    if (cached && (Date.now() - cached.timestamp) < NOTION_DATA_CACHE_DURATION) {
      if (isDevMode) console.log('[fetchNotionDataFromWP] Returning cached data')
      return cached.data
    }
    if (isDevMode && cached) console.log('[fetchNotionDataFromWP] Cache expired, fetching fresh data')
  } else {
    if (isDevMode) console.log('[fetchNotionDataFromWP] Force refresh - skipping cache')
  }

  const params = new URLSearchParams()
  if (opts?.since) params.append('since', opts.since)
  if (opts?.until) params.append('until', opts.until)
  if (opts?.force) {
    params.append('force', '1')
    // Add cache buster to prevent browser/CDN caching
    params.append('_t', Date.now().toString())
  }

  const url = `/wp-json/kj-cockpit/v1/fetch-notion?${params.toString()}`

  const headers: Record<string, string> = {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  }
  // Add WP REST nonce header if provided by server-localized object
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const wp = (window as any).WP_COCKPIT
    const isDev = wp?.devMode || false
    if (isDev) {
      console.log('[fetchNotionDataFromWP] Options:', opts)
      console.log('[fetchNotionDataFromWP] URL:', url)
      console.log('[fetchNotionDataFromWP] WP_COCKPIT object:', wp)
    }
    if (wp && wp.restNonce) {
      headers['X-WP-Nonce'] = String(wp.restNonce)
      if (isDev) console.log('[fetchNotionDataFromWP] Added nonce to headers:', headers['X-WP-Nonce'])
    } else {
      console.error('No restNonce found in WP_COCKPIT')
    }
  } catch (e) {
    console.error('Error accessing WP_COCKPIT:', e)
  }

  const isDev = (window as any).WP_COCKPIT?.devMode || false
  if (isDev) console.log('[fetchNotionDataFromWP] Fetching with headers:', headers)
  
  // Add cache control headers when forcing refresh
  const fetchOptions: RequestInit = { 
    credentials: 'same-origin', 
    headers 
  }
  if (opts?.force) {
    fetchOptions.cache = 'no-store'
  }
  
  const res = await fetch(url, fetchOptions)
  if (!res.ok) {
    const errorText = await res.text()
    console.error('Notion fetch failed:', res.status, errorText)
    throw new Error(`Failed to fetch notion proxy: ${res.status} - ${errorText}`)
  }

  const raw = (await res.json()) as NotionSyncResponse
  // If webhook signals that user not found, propagate as an error response
  if ((raw as any).userFound === 'error') {
    return {
      requestId: raw.requestId,
      status: 'error',
      meta: { message: 'User not found' },
    }
  }

  // Normalize the response so the rest of the app can rely on proper types
  const normalized: NotionSyncResponse = { ...raw }
  if (raw && raw.data) {
    const tasks = raw.data.tasks || []
    const timesheets = raw.data.timesheets || []

    normalized.data = {
      tasks: tasks.map((t: any) => ({
        id: String(t.id),
        title: t.title ?? t.name ?? '',
        status: t.status ?? 'Not Started',
        due: (t.dueDate || t.due) ? new Date(t.dueDate || t.due) : null,
        contributors: Array.isArray(t.contributors) ? t.contributors.map(String) : [],
        description: t.description ?? t.desc ?? '',
        estimatedHours: t.estimatedHours ? parseFloat(t.estimatedHours) : (t.estimated_hours ? parseFloat(t.estimated_hours) : undefined),
        realisedHours: t.realisedHours ? parseFloat(t.realisedHours) : (t.realised_hours ? parseFloat(t.realised_hours) : undefined),
        tags: Array.isArray(t.tags) ? t.tags.map(String) : [],
        url: t.url || t.notion_url || undefined,
        markdownContent: t.markdownContent || t.markdown_content || undefined,
      })),
      timesheets: timesheets.map((s: any) => ({
        id: String(s.id),
        taskId: String(s.taskId),
        userId: s.userId ? String(s.userId) : (s.user ? String(s.user.id || s.user) : ''),
        status: s.status ?? 'Draft',
        startTime: s.startTime ? new Date(s.startTime) : new Date(),
        endTime: s.endTime ? new Date(s.endTime) : null,
        manualMinutes: typeof s.manualMinutes === 'string' ? parseInt(s.manualMinutes, 10) || 0 : Number(s.manualMinutes) || 0,
        isSubmitted: Boolean(s.isSubmitted),
      })),
    }
  }

  // Cache the normalized response
  notionDataCache.set(cacheKey, {
    data: normalized,
    timestamp: Date.now()
  })
  
  if (isDevMode) console.log('[fetchNotionDataFromWP] Cached normalized response')

  return normalized
}

export async function refreshNotionData(): Promise<NotionSyncResponse> {
  return fetchNotionDataFromWP({ force: true })
}

export function clearNotionDataCache(): void {
  notionDataCache.clear()
  const isDevMode = (window as any).WP_COCKPIT?.devMode || false
  if (isDevMode) console.log('[clearNotionDataCache] Cleared all notion data cache')
}

export function clearCommentsCache(): void {
  commentsCache.clear()
  const isDevMode = (window as any).WP_COCKPIT?.devMode || false
  if (isDevMode) console.log('[clearCommentsCache] Cleared all comments cache')
}

export function clearAllCaches(): void {
  clearNotionDataCache()
  clearCommentsCache()
  clearUserTasksCache()
  taskContentCache.clear()
  const isDevMode = (window as any).WP_COCKPIT?.devMode || false
  if (isDevMode) console.log('[clearAllCaches] Cleared all caches')
}

export async function createTimesheetEntry(entry: Omit<NotionTimesheet, "id">): Promise<NotionTimesheet> {
  // TODO: Implement actual Notion API call
  return {
    id: `timesheet-${Date.now()}`,
    ...entry,
  }
}

export async function updateTimesheetEntry(id: string, updates: Partial<NotionTimesheet>): Promise<NotionTimesheet> {
  // TODO: Implement actual Notion API call
  return {
    id,
    ...updates,
  } as NotionTimesheet
}

export async function submitTimesheetEntry(_id: string): Promise<void> {
  // TODO: Implement actual Notion API call to mark as submitted
}

export interface SubmitTimesheetsResponse {
  requestId?: string
  status?: 'ok' | 'error'
  message?: string
  processed?: number
}

// In-memory cache for task content
const taskContentCache = new Map<string, { content: string; timestamp: number }>()
const TASK_CONTENT_CACHE_DURATION = 60 * 60 * 1000 // 60 minutes (1 hour)

// In-memory cache for tasks and timesheets data
const notionDataCache = new Map<string, { data: NotionSyncResponse; timestamp: number }>()
const NOTION_DATA_CACHE_DURATION = 60 * 60 * 1000 // 60 minutes (1 hour)

// In-memory cache for comments data
const commentsCache = new Map<string, { data: Comment[]; timestamp: number }>()
const COMMENTS_CACHE_DURATION = 30 * 60 * 1000 // 30 minutes

export async function fetchTaskContent(taskId: string, force: boolean = false): Promise<{ content: string } | null> {
  const cacheKey = `task_content_${taskId}`
  const isDev = (window as any).WP_COCKPIT?.devMode || false
  
  // Check cache first if not forcing refresh
  if (!force) {
    const cached = taskContentCache.get(cacheKey)
    if (cached && (Date.now() - cached.timestamp) < TASK_CONTENT_CACHE_DURATION) {
      if (isDev) console.log('[fetchTaskContent] Returning cached content for task:', taskId)
      return { content: cached.content }
    }
    if (isDev && cached) console.log('[fetchTaskContent] Cache expired for task:', taskId)
  } else {
    if (isDev) console.log('[fetchTaskContent] Force refresh - skipping cache for task:', taskId)
  }

  const params = new URLSearchParams()
  params.append('taskId', taskId)
  if (force) {
    params.append('force', '1')
    // Add cache buster to prevent browser/CDN caching
    params.append('_t', Date.now().toString())
  }

  const url = `/wp-json/kj-cockpit/v1/fetch-task-content?${params.toString()}`

  const headers: Record<string, string> = {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  }
  
  // Add WP REST nonce header if provided by server-localized object
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const wp = (window as any).WP_COCKPIT
    if (isDev) {
      console.log('[fetchTaskContent] Task ID:', taskId)
      console.log('[fetchTaskContent] URL:', url)
      console.log('[fetchTaskContent] WP_COCKPIT object:', wp)
    }
    if (wp && wp.restNonce) {
      headers['X-WP-Nonce'] = String(wp.restNonce)
      if (isDev) console.log('[fetchTaskContent] Added nonce to headers:', headers['X-WP-Nonce'])
    } else {
      console.error('No restNonce found in WP_COCKPIT')
    }
  } catch (e) {
    console.error('Error accessing WP_COCKPIT:', e)
  }

  if (isDev) console.log('[fetchTaskContent] Fetching with headers:', headers)
  
  const fetchOptions: RequestInit = { 
    credentials: 'same-origin', 
    headers
  }
  
  if (force) {
    fetchOptions.cache = 'no-store'
  }
  
  try {
    const res = await fetch(url, fetchOptions)
    if (!res.ok) {
      const errorText = await res.text()
      console.error('Task content fetch failed:', res.status, errorText)
      if (res.status === 404) {
        return null // Task content not found
      }
      throw new Error(`Failed to fetch task content: ${res.status} - ${errorText}`)
    }

    const data = await res.json()
    if (isDev) console.log('[fetchTaskContent] Response:', data)
    
    const content = data.content || ''
    
    // Cache the result
    taskContentCache.set(cacheKey, {
      content,
      timestamp: Date.now()
    })
    
    if (isDev) console.log('[fetchTaskContent] Cached content for task:', taskId)
    
    return { content }
  } catch (error) {
    console.error('Error fetching task content:', error)
    return null
  }
}

export function clearTaskContentCache(taskId?: string): void {
  if (taskId) {
    const cacheKey = `task_content_${taskId}`
    taskContentCache.delete(cacheKey)
    const isDev = (window as any).WP_COCKPIT?.devMode || false
    if (isDev) console.log('[clearTaskContentCache] Cleared cache for task:', taskId)
  } else {
    taskContentCache.clear()
    const isDev = (window as any).WP_COCKPIT?.devMode || false
    if (isDev) console.log('[clearTaskContentCache] Cleared all task content cache')
  }
}

export async function refreshNotionDataAndClearCache(): Promise<NotionSyncResponse> {
  // Clear all caches when refreshing main data
  clearAllCaches()
  return fetchNotionDataFromWP({ force: true })
}

// In-memory cache for user tasks data (separate from time tracker tasks)
const userTasksCache = new Map<string, { data: NotionTask[]; timestamp: number }>()
const USER_TASKS_CACHE_DURATION = 60 * 60 * 1000 // 60 minutes (1 hour)

export async function fetchUserTasksFromWP(opts?: { force?: boolean }): Promise<NotionTask[]> {
  const isDevMode = (window as any).WP_COCKPIT?.devMode || false
  
  // Create cache key
  const cacheKey = 'user_tasks'
  
  // Check in-memory cache first if not forcing refresh
  if (!opts?.force) {
    const cached = userTasksCache.get(cacheKey)
    if (cached && (Date.now() - cached.timestamp) < USER_TASKS_CACHE_DURATION) {
      if (isDevMode) console.log('[fetchUserTasksFromWP] Returning cached user tasks data')
      return cached.data
    }
    if (isDevMode && cached) console.log('[fetchUserTasksFromWP] Cache expired, fetching fresh user tasks data')
  } else {
    if (isDevMode) console.log('[fetchUserTasksFromWP] Force refresh - skipping cache')
  }

  const params = new URLSearchParams()
  if (opts?.force) {
    params.append('force', '1')
    // Add cache buster to prevent browser/CDN caching
    params.append('_t', Date.now().toString())
  }

  const url = `/wp-json/kj-cockpit/v1/fetch-user-tasks?${params.toString()}`

  const headers: Record<string, string> = {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  }
  
  // Add WP REST nonce header if provided by server-localized object
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const wp = (window as any).WP_COCKPIT
    if (isDevMode) {
      console.log('[fetchUserTasksFromWP] Options:', opts)
      console.log('[fetchUserTasksFromWP] URL:', url)
      console.log('[fetchUserTasksFromWP] WP_COCKPIT object:', wp)
    }
    if (wp && wp.restNonce) {
      headers['X-WP-Nonce'] = String(wp.restNonce)
      if (isDevMode) console.log('[fetchUserTasksFromWP] Added nonce to headers:', headers['X-WP-Nonce'])
    } else {
      console.error('No restNonce found in WP_COCKPIT')
    }
  } catch (e) {
    console.error('Error accessing WP_COCKPIT:', e)
  }

  if (isDevMode) console.log('[fetchUserTasksFromWP] Fetching with headers:', headers)
  
  // Add cache control headers when forcing refresh
  const fetchOptions: RequestInit = { 
    credentials: 'same-origin', 
    headers 
  }
  if (opts?.force) {
    fetchOptions.cache = 'no-store'
  }
  
  const res = await fetch(url, fetchOptions)
  if (!res.ok) {
    const errorText = await res.text()
    console.error('User tasks fetch failed:', res.status, errorText)
    throw new Error(`Failed to fetch user tasks: ${res.status} - ${errorText}`)
  }

  const raw = await res.json()
  
  // If webhook signals that user not found, propagate as an error response
  if ((raw as any).userFound === 'error') {
    throw new Error('User not found')
  }

  // Normalize the response to NotionTask array
  let tasks: NotionTask[] = []
  
  if (raw && raw.data && raw.data.tasks) {
    tasks = raw.data.tasks.map((t: any) => ({
      id: String(t.id),
      title: t.title ?? t.name ?? '',
      status: t.status ?? 'Not Started',
      due: (t.dueDate || t.due) ? new Date(t.dueDate || t.due) : null,
      contributors: Array.isArray(t.contributors) ? t.contributors.map(String) : [],
      description: t.description ?? t.desc ?? '',
      estimatedHours: t.estimatedHours ? parseFloat(t.estimatedHours) : (t.estimated_hours ? parseFloat(t.estimated_hours) : undefined),
      realisedHours: t.realisedHours ? parseFloat(t.realisedHours) : (t.realised_hours ? parseFloat(t.realised_hours) : undefined),
      tags: Array.isArray(t.tags) ? t.tags.map(String) : [],
      url: t.url || t.notion_url || undefined,
      markdownContent: t.markdownContent || t.markdown_content || undefined,
    }))
  } else if (raw && Array.isArray(raw.tasks)) {
    // Handle direct tasks array response
    tasks = raw.tasks.map((t: any) => ({
      id: String(t.id),
      title: t.title ?? t.name ?? '',
      status: t.status ?? 'Not Started',
      due: (t.dueDate || t.due) ? new Date(t.dueDate || t.due) : null,
      contributors: Array.isArray(t.contributors) ? t.contributors.map(String) : [],
      description: t.description ?? t.desc ?? '',
      estimatedHours: t.estimatedHours ? parseFloat(t.estimatedHours) : (t.estimated_hours ? parseFloat(t.estimated_hours) : undefined),
      realisedHours: t.realisedHours ? parseFloat(t.realisedHours) : (t.realised_hours ? parseFloat(t.realised_hours) : undefined),
      tags: Array.isArray(t.tags) ? t.tags.map(String) : [],
      url: t.url || t.notion_url || undefined,
      markdownContent: t.markdownContent || t.markdown_content || undefined,
    }))
  }

  // Cache the normalized response
  userTasksCache.set(cacheKey, {
    data: tasks,
    timestamp: Date.now()
  })
  
  if (isDevMode) console.log('[fetchUserTasksFromWP] Cached normalized user tasks response:', tasks.length, 'tasks')

  return tasks
}

export function clearUserTasksCache(): void {
  userTasksCache.clear()
  const isDevMode = (window as any).WP_COCKPIT?.devMode || false
  if (isDevMode) console.log('[clearUserTasksCache] Cleared user tasks cache')
}

export async function refreshUserTasks(): Promise<NotionTask[]> {
  return fetchUserTasksFromWP({ force: true })
}

export async function submitTimesheetsToWP(timesheets: unknown[], taskStatusUpdates?: Array<{ taskID: string; status: string }>): Promise<SubmitTimesheetsResponse> {
  const url = '/wp-json/kj-cockpit/v1/submit-timesheets'

  const headers: Record<string, string> = {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  }
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const wp = (window as any).WP_COCKPIT
    const isDev = wp?.devMode || false
    if (wp && wp.restNonce) {
      headers['X-WP-Nonce'] = String(wp.restNonce)
      if (isDev) console.log('Submit: Added nonce to headers')
    } else {
      console.error('Submit: No restNonce found')
    }
  } catch (e) {
    console.error('Submit: Error accessing WP_COCKPIT:', e)
  }

  const body: any = { timesheets }
  if (taskStatusUpdates && taskStatusUpdates.length > 0) body['taskStatusUpdates'] = taskStatusUpdates
  const res = await fetch(url, { method: 'POST', credentials: 'same-origin', headers, body: JSON.stringify(body) })
  if (!res.ok) {
    const errorText = await res.text()
    console.error('Submit timesheets failed:', res.status, errorText)
    throw new Error(`Failed to submit timesheets: ${res.status} - ${errorText}`)
  }
  return res.json()
}

// Fetch comments for a task or project
export async function fetchComments(id: string, type: 'task' | 'project', force = false): Promise<Comment[]> {
  const config = (window as any).WP_COCKPIT
  if (!config) throw new Error('WP Cockpit config not available')
  
  const isDevMode = config?.devMode || false
  
  // Create cache key based on type and ID
  const cacheKey = `comments_${type}_${id}`
  
  // Check in-memory cache first if not forcing refresh
  if (!force) {
    const cached = commentsCache.get(cacheKey)
    if (cached && (Date.now() - cached.timestamp) < COMMENTS_CACHE_DURATION) {
      if (isDevMode) console.log('[fetchComments] Returning cached comments for:', type, id)
      return cached.data
    }
    if (isDevMode && cached) console.log('[fetchComments] Cache expired for:', type, id)
  } else {
    if (isDevMode) console.log('[fetchComments] Force refresh - skipping cache for:', type, id)
  }
  
  const url = `${config.restNonce ? '/wp-json' : ''}/kj-cockpit/v1/fetch-comments`
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (config.restNonce) headers['X-WP-Nonce'] = config.restNonce
  
  const params = new URLSearchParams({
    id,
    type,
    force: force.toString()
  })
  
  // Add cache buster to prevent browser/CDN caching when forcing refresh
  if (force) {
    params.append('_cb', Date.now().toString())
  }
  
  const res = await fetch(`${url}?${params}`, {
    method: 'GET',
    credentials: 'same-origin',
    headers,
    cache: force ? 'no-store' : 'default'
  })
  
  if (!res.ok) {
    const errorText = await res.text()
    console.error('Fetch comments failed:', res.status, errorText)
    throw new Error(`Failed to fetch comments: ${res.status} - ${errorText}`)
  }
  
  const response = await res.json() as CommentsResponse
  const comments = response.data?.comments || []
  
  // Cache the response
  commentsCache.set(cacheKey, {
    data: comments,
    timestamp: Date.now()
  })
  
  if (isDevMode) console.log('[fetchComments] Cached fresh comments for:', type, id, `(${comments.length} comments)`)
  
  return comments
}

// Submit a new comment for a task or project
export async function submitComment(id: string, type: 'task' | 'project', comment: string): Promise<{ status: string; message: string }> {
  const config = (window as any).WP_COCKPIT
  if (!config) throw new Error('WP Cockpit config not available')
  
  const isDevMode = config?.devMode || false
  
  const url = `${config.restNonce ? '/wp-json' : ''}/kj-cockpit/v1/submit-comment`
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (config.restNonce) headers['X-WP-Nonce'] = config.restNonce
  
  const body = {
    id,
    type,
    comment
  }
  
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers,
    body: JSON.stringify(body)
  })
  
  if (!res.ok) {
    const errorText = await res.text()
    console.error('Submit comment failed:', res.status, errorText)
    throw new Error(`Failed to submit comment: ${res.status} - ${errorText}`)
  }
  
  const result = await res.json()
  
  // Invalidate the comments cache for this item after successful submission
  const cacheKey = `comments_${type}_${id}`
  commentsCache.delete(cacheKey)
  
  if (isDevMode) console.log('[submitComment] Invalidated comments cache for:', type, id)
  
  return result
}

// Fetch project management data
export async function fetchProductionManagement(force = false): Promise<any> {
  const config = (window as any).WP_COCKPIT
  if (!config) throw new Error('WP Cockpit config not available')
  
  const isDevMode = config?.devMode || false
  const url = `${config.restNonce ? '/wp-json' : ''}/kj-cockpit/v1/fetch-production-management${force ? '?force=1' : ''}`
  
  if (isDevMode) {
    console.log('[fetchProductionManagement] Fetching from:', url)
    console.log('[fetchProductionManagement] Force refresh:', force)
  }
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (config.restNonce) headers['X-WP-Nonce'] = config.restNonce
  
  const res = await fetch(url, {
    method: 'GET',
    credentials: 'same-origin',
    headers,
  })
  
  if (!res.ok) {
    const errorText = await res.text()
    console.error('Fetch project management failed:', res.status, errorText)
    throw new Error(`Failed to fetch project management data: ${res.status} - ${errorText}`)
  }
  
  const data = await res.json()
  
  if (isDevMode) {
    console.log('[fetchProductionManagement] Received data:', data)
  }
  
  return data
}

// Fetch sprint tasks data
export async function fetchSprintTasks(force = false): Promise<any> {
  const config = (window as any).WP_COCKPIT
  if (!config) throw new Error('WP Cockpit config not available')
  
  const isDevMode = config?.devMode || false
  const url = `${config.restNonce ? '/wp-json' : ''}/kj-cockpit/v1/fetch-sprint-tasks${force ? '?force=1' : ''}`
  
  if (isDevMode) {
    console.log('[fetchSprintTasks] Fetching from:', url)
    console.log('[fetchSprintTasks] Force refresh:', force)
  }
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (config.restNonce) headers['X-WP-Nonce'] = config.restNonce
  
  const res = await fetch(url, {
    method: 'GET',
    credentials: 'same-origin',
    headers,
  })
  
  if (!res.ok) {
    const errorText = await res.text()
    console.error('Fetch sprint tasks failed:', res.status, errorText)
    throw new Error(`Failed to fetch sprint tasks: ${res.status} - ${errorText}`)
  }
  
  const data = await res.json()
  
  if (isDevMode) {
    console.log('[fetchSprintTasks] Received data:', data)
  }
  
  return data
}

// Assign a sprint to a task
export async function assignTaskSprint(taskId: string, sprintId: string): Promise<{ success: boolean; message: string }> {
  const config = (window as any).WP_COCKPIT
  if (!config) throw new Error('WP Cockpit config not available')
  
  const isDevMode = config?.devMode || false
  const url = `${config.restNonce ? '/wp-json' : ''}/kj-cockpit/v1/assign-task-sprint`
  
  if (isDevMode) {
    console.log('[assignTaskSprint] Assigning sprint to task')
    console.log('[assignTaskSprint] Task ID:', taskId)
    console.log('[assignTaskSprint] Sprint ID:', sprintId)
  }
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (config.restNonce) headers['X-WP-Nonce'] = config.restNonce
  
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers,
    body: JSON.stringify({ taskId, sprintId }),
  })
  
  if (!res.ok) {
    const errorText = await res.text()
    console.error('Assign task sprint failed:', res.status, errorText)
    throw new Error(`Failed to assign sprint: ${res.status} - ${errorText}`)
  }
  
  const data = await res.json()
  
  if (isDevMode) {
    console.log('[assignTaskSprint] Response:', data)
  }
  
  return data
}

// Update task properties
export interface UpdateTaskPayload {
  taskId: string;
  projectId?: string;
  contributors?: string[];
  tags?: string[];
  estimatedHours?: number; // Keep for backwards compatibility, sum of department hours
  estimatedHoursCREA?: number;  // Creative
  estimatedHoursCONSLT?: number; // Consulting
  estimatedHoursMKT?: number;   // Marketing
  estimatedHoursTECH?: number;  // Tech
  estimatedHoursBIZ?: number;   // Business
  estimatedHoursKAIZEN?: number; // Kaizen
  dueDate?: string;
  sprintId?: string;
}

export async function updateTask(payload: UpdateTaskPayload): Promise<{ success: boolean; message: string }> {
  const config = (window as any).WP_COCKPIT
  if (!config) throw new Error('WP Cockpit config not available')
  
  const isDevMode = config?.devMode || false
  const url = `${config.restNonce ? '/wp-json' : ''}/kj-cockpit/v1/update-task`
  
  if (isDevMode) {
    console.log('[updateTask] Updating task')
    console.log('[updateTask] Payload:', payload)
  }
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (config.restNonce) headers['X-WP-Nonce'] = config.restNonce
  
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers,
    body: JSON.stringify(payload),
  })
  
  if (!res.ok) {
    const errorText = await res.text()
    console.error('Update task failed:', res.status, errorText)
    throw new Error(`Failed to update task: ${res.status} - ${errorText}`)
  }
  
  const data = await res.json()
  
  if (isDevMode) {
    console.log('[updateTask] Response:', data)
  }
  
  return data
}

// Fetch resources data
export async function fetchResources(force = false): Promise<any> {
  const config = (window as any).WP_COCKPIT
  if (!config) throw new Error('WP Cockpit config not available')
  
  const isDevMode = config?.devMode || false
  const url = `${config.restNonce ? '/wp-json' : ''}/kj-cockpit/v1/fetch-resources${force ? '?force=1' : ''}`
  
  if (isDevMode) {
    console.log('[fetchResources] Fetching from:', url)
    console.log('[fetchResources] Force refresh:', force)
  }
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (config.restNonce) headers['X-WP-Nonce'] = config.restNonce
  
  const res = await fetch(url, {
    method: 'GET',
    credentials: 'same-origin',
    headers,
  })
  
  if (!res.ok) {
    const errorText = await res.text()
    console.error('Fetch resources failed:', res.status, errorText)
    throw new Error(`Failed to fetch resources data: ${res.status} - ${errorText}`)
  }
  
  const data = await res.json()
  
  if (isDevMode) {
    console.log('[fetchResources] Received data:', data)
  }
  
  return data
}

// Fetch all timesheets for production management (everyone's time entries)
export async function fetchAllTimesheets(force = false): Promise<any> {
  const config = (window as any).WP_COCKPIT
  if (!config) throw new Error('WP Cockpit config not available')
  
  const isDevMode = config?.devMode || false
  const url = `${config.restNonce ? '/wp-json' : ''}/kj-cockpit/v1/fetch-all-timesheets${force ? '?force=1' : ''}`
  
  if (isDevMode) {
    console.log('[fetchAllTimesheets] Fetching from:', url)
    console.log('[fetchAllTimesheets] Force refresh:', force)
  }
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (config.restNonce) headers['X-WP-Nonce'] = config.restNonce
  
  const res = await fetch(url, {
    method: 'GET',
    credentials: 'same-origin',
    headers,
  })
  
  if (!res.ok) {
    const errorText = await res.text()
    console.error('Fetch all timesheets failed:', res.status, errorText)
    throw new Error(`Failed to fetch timesheets: ${res.status} - ${errorText}`)
  }
  
  const data = await res.json()
  
  if (isDevMode) {
    console.log('[fetchAllTimesheets] Received data:', data)
  }
  
  return data
}

// Search for a resource by email
export async function searchResource(email: string): Promise<any> {
  const config = (window as any).WP_COCKPIT
  if (!config) throw new Error('WP Cockpit config not available')
  
  const isDevMode = config?.devMode || false
  const url = `${config.restNonce ? '/wp-json' : ''}/kj-cockpit/v1/search-resource`
  
  if (isDevMode) {
    console.log('[searchResource] Searching for email:', email)
  }
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (config.restNonce) headers['X-WP-Nonce'] = config.restNonce
  
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers,
    body: JSON.stringify({ email }),
  })
  
  if (!res.ok) {
    const errorText = await res.text()
    console.error('Search resource failed:', res.status, errorText)
    throw new Error(`Failed to search resource: ${res.status} - ${errorText}`)
  }
  
  const data = await res.json()
  
  if (isDevMode) {
    console.log('[searchResource] Received data:', data)
  }
  
  return data
}

// Add a new resource
export async function addResource(resourceData: any): Promise<any> {
  const config = (window as any).WP_COCKPIT
  if (!config) throw new Error('WP Cockpit config not available')
  
  const isDevMode = config?.devMode || false
  const url = `${config.restNonce ? '/wp-json' : ''}/kj-cockpit/v1/add-resource`
  
  if (isDevMode) {
    console.log('[addResource] Adding/Editing resource:', resourceData)
  }
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (config.restNonce) headers['X-WP-Nonce'] = config.restNonce
  
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers,
    body: JSON.stringify({ 
      resource: resourceData,
      action: resourceData.action || 'ADD'
    }),
  })
  
  if (!res.ok) {
    const errorText = await res.text()
    console.error('Add/Edit resource failed:', res.status, errorText)
    throw new Error(`Failed to ${resourceData.action === 'EDIT' ? 'update' : 'add'} resource: ${res.status} - ${errorText}`)
  }
  
  const data = await res.json()
  
  if (isDevMode) {
    console.log('[addResource] Resource operation successful:', data)
  }
  
  return data
}

export async function fetchRoles(): Promise<any> {
  const config = (window as any).WP_COCKPIT
  if (!config) throw new Error('WP Cockpit config not available')
  
  const isDevMode = config?.devMode || false
  const url = `${config.restNonce ? '/wp-json' : ''}/kj-cockpit/v1/fetch-roles`
  
  if (isDevMode) {
    console.log('[fetchRoles] Fetching roles')
  }
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (config.restNonce) headers['X-WP-Nonce'] = config.restNonce
  
  const res = await fetch(url, {
    method: 'GET',
    credentials: 'same-origin',
    headers,
  })
  
  if (!res.ok) {
    const errorText = await res.text()
    console.error('Fetch roles failed:', res.status, errorText)
    throw new Error(`Failed to fetch roles: ${res.status} - ${errorText}`)
  }
  
  const data = await res.json()
  
  if (isDevMode) {
    console.log('[fetchRoles] Roles fetched successfully:', data)
  }
  
  return data
}
